import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, Quote } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_REVIEW_URL = "https://search.google.com/local/reviews?placeid=ChIJY6jc-ZVbkWsR_tJ8nLuVa2I";

export const GoogleReviewSection = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total_reviews: 0, average_rating: 0, five_star_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/api/public/google-reviews`);
        setReviews(res.data.reviews || []);
        setStats(res.data.stats || { total_reviews: 0, average_rating: 0, five_star_count: 0 });
      } catch (err) {
        console.error("Failed to fetch reviews");
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} size={14} className={i < rating ? "text-yellow-400 fill-yellow-400" : "text-zinc-700"} />
      ))}
    </div>
  );

  return (
    <section data-testid="google-review-section" className="py-20 md:py-28 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[1px] bg-[#f59e0b]" />
            <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">Testimonials</span>
            <div className="w-12 h-[1px] bg-[#f59e0b]" />
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-white mb-4">
            What Our Customers Say
          </h2>
          {stats.total_reviews > 0 && (
            <div className="flex items-center justify-center gap-6 text-zinc-400">
              <div className="flex items-center gap-2">
                <Star size={18} className="text-yellow-400 fill-yellow-400" />
                <span className="font-heading text-xl text-white">{stats.average_rating}</span>
                <span className="text-sm">rating</span>
              </div>
              <span className="text-zinc-700">|</span>
              <span className="text-sm">{stats.total_reviews} reviews</span>
            </div>
          )}
        </motion.div>

        {/* Reviews Grid */}
        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {reviews.slice(0, 6).map((review, index) => (
              <motion.div
                key={review.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#09090b] border border-white/5 p-6 relative"
              >
                <Quote size={24} className="absolute top-4 right-4 text-[#f59e0b]/20" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#f59e0b]/20 border border-[#f59e0b]/30 flex items-center justify-center text-[#f59e0b] font-heading text-sm">
                    {review.reviewer_name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="font-heading text-sm text-white">{review.reviewer_name}</p>
                    <div className="flex items-center gap-2">
                      {renderStars(review.rating)}
                      {review.review_date && (
                        <span className="text-xs text-zinc-600">{review.review_date}</span>
                      )}
                    </div>
                  </div>
                </div>
                {review.review_text && (
                  <p className="text-zinc-400 font-body text-sm leading-relaxed line-clamp-4">
                    "{review.review_text}"
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        ) : !loading ? (
          <div className="text-center py-12 mb-10 border border-white/5 bg-[#09090b]">
            <Star size={32} className="text-[#f59e0b]/30 mx-auto mb-4" />
            <p className="text-zinc-400 font-body mb-2">Our Google reviews will appear here</p>
            <p className="text-zinc-600 text-sm font-body">Check out our reviews on Google in the meantime!</p>
          </div>
        ) : null}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row items-center justify-between gap-8 bg-gradient-to-r from-[#f59e0b]/10 to-transparent border border-[#f59e0b]/20 p-8 md:p-10"
        >
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-heading text-xl sm:text-2xl uppercase tracking-tight text-white mb-2">
              Happy with our work?
            </h3>
            <p className="text-zinc-500 font-body text-sm max-w-md">
              Your review helps other car enthusiasts find us. We'd love to hear about your experience.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-btn"
              className="flex items-center gap-3 bg-white text-black font-heading text-sm tracking-[0.15em] uppercase px-6 py-3 hover:bg-zinc-200 transition-colors no-underline"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Leave a Review
            </a>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-body transition-colors no-underline"
            >
              <ExternalLink size={14} />
              See all reviews
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
